import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { DO_LOADING } from 'redux/actions/loading';
import { HIT_BOARD_STATUS } from 'redux/actions/board';
import {
  apiCompetencyLibrary, apiSkillGroup, compLibraryService, skillGroupService,
} from '../../../service/competencyAndSkill.service';

const useReduxBridge = () => {
  const dispatch = useDispatch();

  const setLoading = (value: boolean) => dispatch({ type: DO_LOADING, payload: value });

  const status = (statusValue: 'success' | 'failed', message: string) => dispatch({
    type: HIT_BOARD_STATUS,
    payload: { status: statusValue, message },
  });

  return {
    setLoading,
    status,
  };
};

export const useSkillGroupList = (companyId: null | string, enabled: boolean = true) => useQuery({
  queryKey: [apiSkillGroup.skillGroupList, companyId],
  queryFn: () => skillGroupService.getSkillGroup(),
  enabled: !!companyId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
  placeholderData: [],
});

export const useSubSkillGroupList = (skillGroupId: null | string, enabled: boolean = true) => useQuery({
  queryKey: [apiSkillGroup.subSkillGroupList, skillGroupId],
  queryFn: () => skillGroupService.getSubSkillGroup({
    competency_skill_group_id: skillGroupId,
  }),
  enabled: !!skillGroupId && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  placeholderData: [],
});

const useMutationCategorySkillGroup = <
TPayload, TResponse extends { message: string },
>(
    mutationFn: (payload: TPayload) => Promise<TResponse>,
  ) => {
  const bridge = useReduxBridge();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (responseMessage: TResponse) => {
      const successMessage = responseMessage.message;

      void queryClient.invalidateQueries({
        queryKey: [apiSkillGroup.skillGroupList],
      });

      bridge.status('success', successMessage);
    },
    onError: (error) => {
      const errorMessage = error.message;
      bridge.status('failed', errorMessage);
    },
    onSettled: () => bridge.setLoading(false),
  });
};

export const useMutationSkillGroupManagement = () => useMutationCategorySkillGroup(skillGroupService.mutationSkillGroupManagement);
export const useDeleteSkillGroup = () => useMutationCategorySkillGroup(skillGroupService.deleteSkillGroup);

export const useCompLibraryList = (companyId: null | string, type: null | string, enabled: boolean = true) => useQuery({
  queryKey: [apiCompetencyLibrary.compLibraryList, companyId, type],
  queryFn: () => compLibraryService.getCompLibrary({
    company_id: companyId,
    status: 'active',
    type,
  }),
  enabled: !!companyId && !!type && enabled,
  retry: false,
  refetchOnWindowFocus: false,
  staleTime: 5 * 60 * 1000,
  placeholderData: [],
});

const useMutationCategoryCompLibrary = <
TPayload, TResponse extends { message: string },
>(
    mutationFn: (payload: TPayload) => Promise<TResponse>,
  ) => {
  const bridge = useReduxBridge();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onMutate: () => bridge.setLoading(true),
    onSuccess: (responseMessage: TResponse) => {
      const successMessage = responseMessage.message;

      void queryClient.invalidateQueries({
        queryKey: [apiCompetencyLibrary.compLibraryList],
      });

      bridge.status('success', successMessage);
    },
    onError: (error) => {
      const errorMessage = error.message;
      bridge.status('failed', errorMessage);
    },
    onSettled: () => bridge.setLoading(false),
  });
};

export const useCreateCompLibrary = () => useMutationCategoryCompLibrary(compLibraryService.createCompLibrary);

export const useUpdateCompLibrary = () => useMutationCategoryCompLibrary(compLibraryService.updateCompLibrary);

export const useDeleteCompLibrary = () => useMutationCategoryCompLibrary(compLibraryService.deleteCompLibrary);

export const useMoveCompLibrary = () => useMutationCategoryCompLibrary(compLibraryService.moveCompLibrary);
